import { P } from '@amzn/stencil-react-components/text';

export const WITHOUT_USERS_COLUMN_LIST = [
  'log_month',
  'application',
  'application_type',
  'team_name',
  'supervisor_login_name_hierarchy',
  'supervisor_name_hierarchy'
];

export const WITHOUT_USERS_AGGREGATE_COLUMN_LISTS = [
  { name: 'measure_value', operation: 'sum' },
  { name: 'distinct_users', operation: 'count' }
];

export const APPLICATION_FOOTPRINT_COLUMN_LISTS = ['application_type', 'team_name'];

export const APPLICATION_USER_VIEW_COLUMN_LISTS = [...APPLICATION_FOOTPRINT_COLUMN_LISTS, 'user_login'];

export const APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS = [
  { name: 'measure_value', operation: 'sum' },
  { name: 'active_since', operation: 'minDate' },
  { name: 'last_active', operation: 'maxDate' }
];

export const SUB_APPLICATION_FOOTPRINT_COLUMN_LISTS = [...APPLICATION_FOOTPRINT_COLUMN_LISTS, 'application'];

export const SUB_APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS = [
  { name: 'measure_value', operation: 'sum' },
  { name: 'active_since', operation: 'minDate' },
  { name: 'user_login', operation: 'countDistinct' }
];

export const APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS = [...SUB_APPLICATION_FOOTPRINT_MEASURE_COLUMN_LISTS];

export const SUB_APPLICATION_USER_VIEW_COLUMN_LISTS = [...SUB_APPLICATION_FOOTPRINT_COLUMN_LISTS, 'user_login', 'active_since', 'last_active'];

export const SUB_APPLICATION_USER_VIEW_MEASURE_COLUMN_LISTS = [{ name: 'measure_value', operation: 'sum' }];

export const APPLICATION = 'application';

export const USER_METRICS_DASHBOARD_COLUMN_LIST = ['application_type', 'log_month'];

export const USER_METRICS_DASHBOARD_MEASURE_COLUMN_LIST = [
  { name: 'user_login', operation: 'countDistinct' },
  { name: 'measure_value', operation: 'sum' }
];

export const USER_METRICS_TOP_5_APPLICATION_BY_MONTH_COLUMN_LIST = ['application', 'log_month'];

export const USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST = [{ name: 'user_login', operation: 'countDistinct' }];

export const USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_COLUMN_LIST = ['log_month', 'application_type'];

export const USER_METRICS_TOTAL_UNIQUE_USERS_BY_MONTH_MEASURE_COLUMN_LIST = [...USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST];

export const USER_METRICS_AGG_APPLICATION_BY_USERS_COLUMN_LIST = ['application_type', 'create_date_formatted'];

export const USER_METRICS_AGG_SUB_APPLICATION_COLUMN_LIST = ['application', ...USER_METRICS_AGG_APPLICATION_BY_USERS_COLUMN_LIST];

export const USER_METRICS_AGG_APPLICATION_BY_USERS_MEASURE_COLUMN_LIST = [...USER_METRICS_TOP_5_APPLICATION_BY_MONTH_MEASURE_COLUMN_LIST];

export const DAS_FINTECH_ORG_MANAGER = 'Michael DeGard';

export const DAS_FINTECH_ORG_MANAGER_ALIAS = 'degardm';

// Target job levels for manager aggregation (7, 8, 9, 10 - level 9 included for future-proofing)
export const MANAGER_TARGET_JOB_LEVELS = [7, 8, 9, 10, 11, 12];

// ===== Hierarchy Metrics Tab Constants =====

// View mode options for hierarchy display
export const HIERARCHY_VIEW_MODE = {
  MANAGER: 'manager_view',
  APPLICATION: 'app_view'
} as const;
// View mode options for application display in hierarchy display
export const APPLICATION_HIERARCHY_VIEW_MODE = {
  APPLICATION: 'application',
  SUB_APPLICATION: 'sub_application'
};

export type HierarchyViewMode = (typeof HIERARCHY_VIEW_MODE)[keyof typeof HIERARCHY_VIEW_MODE];
export type ApplicationViewMode = (typeof APPLICATION_HIERARCHY_VIEW_MODE)[keyof typeof APPLICATION_HIERARCHY_VIEW_MODE];

// Default manager level filter
export const DEFAULT_MANAGER_LEVEL = 11;

// UI dimensions
export const HIERARCHY_GRID_HEIGHT = '60vh';
export const DETAIL_PANEL_HEIGHT = '519px';

// Base column definitions for User Details grid (shared between Application and Sub Application views)
// Note: cellRenderer for user_login and valueFormatter/valueGetter must be added in component since they use React/JSX
export const USER_DETAILS_BASE_COLUMNS = [
  { field: 'application_type', headerName: 'Application', flex: 1 },
  { field: 'user_login', headerName: 'User Alias', flex: 1 },
  { field: 'team_name', headerName: 'Team', flex: 1 },
  { field: 'active_since', headerName: 'Active Since', flex: 1 },
  { field: 'last_active', headerName: 'Last Accessed', flex: 1 },
  { field: 'measure_value', headerName: 'View Count', flex: 1 },
  { field: 'manager_name_hierarchy_concat', headerName: 'Leadership Hierarchy', flex: 2 }
];

// Sub Application column (prepended in Sub Application view)
export const SUB_APPLICATION_COLUMN = { field: 'application', headerName: 'Sub Application', flex: 1 };

// ===== Hierarchy Grid Configuration Constants =====

// Default column definition for hierarchy grid
export const HIERARCHY_DEFAULT_COL_DEF = {
  flex: 1,
  minWidth: 100,
  sortable: true,
  filter: true,
  resizable: true
};

// Locale text for AG-Grid
export const HIERARCHY_LOCALE_TEXT = {
  rowGroupColumnsEmptyMessage: 'Drag here for grouping'
};

// Grid container style
export const HIERARCHY_GRID_STYLE = {
  height: HIERARCHY_GRID_HEIGHT,
  width: '100%'
};

// ===== Export Processing Utility =====

/**
 * Processes cell values for Excel export from AG-Grid.
 * Handles arrays, objects, null values, and skips the details column.
 */
export const processExcelCellValue = (cellParams: any): string => {
  // Skip the details column by returning empty string
  if (cellParams.column.getColId() === 'details') {
    return '';
  }
  // For the auto-group column, return the full path for proper hierarchy display in Excel
  if (cellParams.column.getColId() === 'ag-Grid-AutoColumn') {
    if (cellParams.node?.data?.path && Array.isArray(cellParams.node.data.path)) {
      return cellParams.node.data.path.join(' > ');
    }
  }
  // Handle null/undefined
  if (cellParams.value === null || cellParams.value === undefined) {
    return '';
  }
  // Convert arrays to strings (for path data)
  if (Array.isArray(cellParams.value)) {
    return cellParams.value.join(' > ');
  }
  // Convert objects to strings
  if (typeof cellParams.value === 'object') {
    return String(cellParams.value);
  }
  // Convert numbers to strings for Excel export
  if (typeof cellParams.value === 'number') {
    return String(cellParams.value);
  }
  return cellParams.value;
};

export const DAS_FIN_SUITE_WEBSITE = 'DasFinSuite-website';
